/* spec-stats.js — <spec-stats> (sections/spec-stats.liquid).

   Two small jobs:

   * Count-up. Once, when the section's top reaches the top third of the
     screen, every whole-number figure runs from its start value to its target
     over data-duration ms on an ease-out-quad. The running value is written to
     an aria-hidden ticker painted over the real digits ([data-counting]), so
     the accessible text is the final figure throughout. Skipped in the theme
     editor and under reduced motion, where the figures simply stand.

   * Keyboard access to the phone strip. Below 750px the stats scroll sideways
     and hold nothing focusable, so the strip itself takes a tab stop while —
     and only while — it actually overflows (WCAG 2.1.1). */

const easeOutQuad = (x) => x * (2 - x);

class SpecStats extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-spec-list]');
    if (this.list && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.syncScrollable());
      this.resizeObserver.observe(this.list);
    }
    this.initCount();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.observer) this.observer.disconnect();
    if (this.frame) window.cancelAnimationFrame(this.frame);
  }

  syncScrollable() {
    const scrollable = this.list.scrollWidth > this.list.clientWidth + 1;
    if (scrollable) this.list.setAttribute('tabindex', '0');
    else this.list.removeAttribute('tabindex');
  }

  initCount() {
    if (!this.hasAttribute('data-animate') || this.started) return;
    this.numbers = Array.from(this.querySelectorAll('[data-count-to]'));
    if (!this.numbers.length || !('IntersectionObserver' in window)) return;
    if (window.Shopify && window.Shopify.designMode) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.tickers = this.numbers.map((el) => el.querySelector('.fs-spec__ticker'));
    this.from = this.numbers.map((el) => parseFloat(el.dataset.countFrom) || 0);
    this.to = this.numbers.map((el) => parseFloat(el.dataset.countTo) || 0);
    this.paint(0);
    this.toggleAttribute('data-counting', true);

    /* The source's trigger is the section's top reaching the top third of the
       screen. A section near the end of a page may never get that high, so it
       also starts once the whole section is in view; and a page restored
       below it (reload, back navigation) shows the final figures at once.
       Thresholds in tenths keep the callback coming while it scrolls in. */
    this.observer = new IntersectionObserver(
      () => {
        const rect = this.getBoundingClientRect();
        const height = window.innerHeight;
        if (rect.bottom <= 0) {
          this.observer.disconnect();
          this.started = true;
          this.removeAttribute('data-counting');
          return;
        }
        if (rect.top > height / 3 && rect.bottom > height) return;
        this.observer.disconnect();
        this.start();
      },
      { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] }
    );
    this.observer.observe(this);
  }

  paint(progress) {
    const eased = easeOutQuad(progress);
    this.tickers.forEach((ticker, i) => {
      if (!ticker) return;
      ticker.dataset.value = String(Math.round(this.from[i] + (this.to[i] - this.from[i]) * eased));
    });
  }

  start() {
    this.started = true;
    const duration = parseInt(this.dataset.duration, 10) || 1500;
    const began = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - began) / duration);
      this.paint(progress);
      if (progress < 1) {
        this.frame = window.requestAnimationFrame(step);
        return;
      }
      this.frame = 0;
      this.removeAttribute('data-counting');
    };
    this.frame = window.requestAnimationFrame(step);
  }
}

if (!customElements.get('spec-stats')) customElements.define('spec-stats', SpecStats);
